import { useCallback } from 'react';
import { purchasingAPI, coreAPI } from '../lib/api';
import type { ASNScanParams, ASNScanResolution } from '../lib/api';
import { useView } from '../contexts/ViewContext';
import { useMenuSettings } from '../contexts/MenuSettingsContext';
import { toast } from 'sonner';

/** Why a scan could not open its ASN; `error` is a failed lookup. */
export type AsnScanBlockedReason = 'not_submitted' | 'no_access' | 'error';

/** Where a resolved scan leaves the user. */
export type AsnScanResult =
  /** Go here; any site switch has already been saved. */
  | { kind: 'navigate'; to: string; asnId: number }
  /** The scan cannot open anything — show this instead of a list. */
  | { kind: 'blocked'; reason: AsnScanBlockedReason; title: string; description: string }
  /** The portal side is switching and the page is reloading into the destination. */
  | { kind: 'reloading' };

// One portal switch per ASN per tab: if the reload still lands on the wrong
// side, the second answer is treated as no access rather than looping.
const PORTAL_SWITCH_KEY = 'helix:asn-scan-portal-switch';

function readPortalSwitch(): string | null {
  try { return sessionStorage.getItem(PORTAL_SWITCH_KEY); } catch { return null; }
}

function writePortalSwitch(value: string | null): void {
  try {
    if (value === null) sessionStorage.removeItem(PORTAL_SWITCH_KEY);
    else sessionStorage.setItem(PORTAL_SWITCH_KEY, value);
  } catch { /* storage unavailable: the switch just is not guarded */ }
}

/**
 * The message for a scan that cannot open its ASN (CR-7037).
 *
 * @param outcome - `not_submitted`, `no_access`, or `error` for a failed lookup
 * @param asnNumber - The scanned ASN number, when the scan carried one
 * @returns The reason, title and description to show in place of the record
 */
export function asnScanBlockedMessage(
  outcome: AsnScanBlockedReason,
  asnNumber: string | null,
): { reason: AsnScanBlockedReason; title: string; description: string } {
  const label = asnNumber ? `ASN ${asnNumber}` : 'This ASN';
  if (outcome === 'not_submitted') {
    return {
      reason: outcome,
      title: 'This ASN has not been submitted yet',
      description: `${label} is still a draft with the supplier. It will open here once they submit it.`,
    };
  }
  if (outcome === 'no_access') {
    return {
      reason: outcome,
      title: 'You do not have access to this ASN',
      description: `${label} is not on any site your account can view. If you need it, ask your administrator for access.`,
    };
  }
  return {
    reason: 'error',
    title: 'Could not open this ASN',
    description: 'Something went wrong looking it up. Please try again.',
  };
}

/**
 * Follow a scanned ASN QR code, or an ASN link that would not open (CR-7037).
 *
 * Asks the server what the code opens for this user, switches site (or portal
 * side, for a dual-sided company) when the record lives there, and returns
 * where to go — or why it cannot be opened, so nobody is left on the ASN list
 * wondering what happened. A scanned line goes to its PO line, or to the ASN
 * when the PO Lines tab is switched off for this tenant.
 *
 * @example
 * const resolveScan = useAsnScanResolver();
 * const result = await resolveScan({ asn_number: 'ASN000123', line: 42 });
 * if (result.kind === 'navigate') navigate(result.to, { replace: true });
 *
 * @returns A function resolving one scan to an {@link AsnScanResult}
 */
export function useAsnScanResolver(): (params: ASNScanParams) => Promise<AsnScanResult> {
  const { selectSite } = useView();
  const { purchasingTabs } = useMenuSettings();
  const poLinesVisible = purchasingTabs.po_lines;

  return useCallback(async (params: ASNScanParams): Promise<AsnScanResult> => {
    let res: ASNScanResolution;
    try {
      res = (await purchasingAPI.resolveASNScan(params)).data;
    } catch (err: unknown) {
      console.error('ASN scan lookup failed:', err);
      return { kind: 'blocked', ...asnScanBlockedMessage('error', params.asn_number ?? null) };
    }

    if (res.outcome === 'not_submitted' || res.outcome === 'no_access' || res.asn_id === null) {
      writePortalSwitch(null);
      const outcome = res.outcome === 'not_submitted' ? 'not_submitted' : 'no_access';
      return { kind: 'blocked', ...asnScanBlockedMessage(outcome, res.asn_number) };
    }

    const asnId = res.asn_id;
    const to = res.destination === 'po_line' && res.po_line_id !== null && poLinesVisible
      ? `/purchasing?tab=po_lines&line=${res.po_line_id}`
      : `/purchasing?tab=asn&asn=${asnId}`;

    if (res.outcome === 'switch_portal' && res.site && res.portal_mode) {
      if (readPortalSwitch() === String(asnId)) {
        writePortalSwitch(null);
        return { kind: 'blocked', ...asnScanBlockedMessage('no_access', res.asn_number) };
      }
      writePortalSwitch(String(asnId));
      try {
        // A portal switch reloads the app, as the top-bar toggle does — saved
        // first, with the site, so the reload lands on the record.
        await coreAPI.updateViewContext({
          current_view_level: 'SITE', current_group: null, current_company: null,
          current_site: res.site.id, portal_mode: res.portal_mode,
        });
      } catch (err: unknown) {
        console.error('Failed to switch portal for ASN scan:', err);
        writePortalSwitch(null);
        return { kind: 'blocked', ...asnScanBlockedMessage('error', res.asn_number) };
      }
      window.location.assign(to);
      return { kind: 'reloading' };
    }

    writePortalSwitch(null);
    if (res.outcome === 'switch_site' && res.site) {
      await selectSite(res.site.id, res.site.name);
      // Long enough to still be showing once the record has opened over the list.
      toast.info(`Switched to ${res.site.name}`, {
        description: `ASN ${res.asn_number} belongs to this site.`,
        duration: 8000,
      });
    }
    return { kind: 'navigate', to, asnId };
  }, [selectSite, poLinesVisible]);
}
